// Lecteur WebRTC (WHEP) : l'offre SDP passe par l'API (/api/inputs/<id>/whep), même origine que la page,
// puis la vidéo arrive en direct de MediaMTX (UDP 8189). Latence typique sous la seconde.
import { notifyUnauthorized } from "../api/client";

const ICE_GATHERING_TIMEOUT = 2000;

function waitIceGathering(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", check);
      resolve();
    };
    const check = () => pc.iceGatheringState === "complete" && done();
    pc.addEventListener("icegatheringstatechange", check);
    setTimeout(done, ICE_GATHERING_TIMEOUT); // on part avec les candidats déjà trouvés
  });
}

export interface WhepSession {
  close(): void;
}

export async function startWhep(
  inputId: string,
  video: HTMLVideoElement,
  onState: (state: RTCPeerConnectionState) => void,
): Promise<WhepSession> {
  const pc = new RTCPeerConnection();
  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });
  pc.ontrack = (e) => {
    if (e.streams[0] && video.srcObject !== e.streams[0]) video.srcObject = e.streams[0];
  };
  pc.onconnectionstatechange = () => onState(pc.connectionState);

  try {
    await pc.setLocalDescription(await pc.createOffer());
    await waitIceGathering(pc);
    const res = await fetch(`/api/inputs/${encodeURIComponent(inputId)}/whep`, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      credentials: "same-origin",
      body: pc.localDescription?.sdp ?? "",
    });
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `preview indisponible (${res.status})`);
    }
    await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });
  } catch (e) {
    pc.close();
    throw e;
  }

  return {
    close() {
      pc.close();
      video.srcObject = null;
    },
  };
}
